import api from '../../services/api';
import {utilsCreateTemporaryLinkToDownload} from '@/utils';

export default {
  state: {
    initiators: [],
    eventProcessorsList: [],
    schedulesList: [],
    eventTypesList: [],
    rulesList: [],
    processorAssociations: [],
    processorDetail: null
  },
  getters: {
    getInitiators(state) {
      return [...state.initiators.map(
        elem => elem.name
      )].sort((a, b) => a.localeCompare(b))
    },
    getInitiatorsWithDefault(state) {
      let initiators = state.initiators.sort((a, b) => a.code.localeCompare(b.code))
      initiators.splice(0, 0, {
        code: '',
        name: 'DEFAULT'
      })

      return initiators
    },
    getProcessorAssociationInitiators(state) {
      let initiators = state.processorAssociations
      let initiatorList = initiators.map(initiator => initiator.name || 'DEFAULT')
      return initiatorList
    },
    getEventTypes(state) {
      return [...state.eventTypesList.map(
        elem => elem.type
      )].sort((a, b) => a.localeCompare(b))
    }
  },
  mutations: {
    setInitiators(state, payload) {
      state.initiators = payload;
    },
    setEventProcessors(state, payload){
      state.eventProcessorsList = payload
    },
    setSchedules(state, payload) {
      state.schedulesList = payload
    },
    setEventTypes(state, payload) {
      state.eventTypesList = payload
    },
    setRules(state, payload){
      state.rulesList = payload
    },
    setProcessorAssociations(state, payload){
      state.processorAssociations = payload
    },
    setProcessorDetail(state, payload){
      state.processorDetail = payload
    }
  },
  actions: {
    apiGetInitiators({commit}) {
      return new Promise((resolve, reject) => {
        api.get('initiators')
        .then((data) => {
          commit('setInitiators', data.data)
          resolve(data.data)
        }).catch(error => {
          reject(error)
        })
      })
    },
    async apiCreateInitiator({commit}, payload) {
      await api.post('/initiators', payload)
    },
    async apiUpdateInitiator({commit}, payload) {
      const {uuid, ...data} = payload
      await api.put(`/initiators/${uuid}`, data)
    },
    async apiDeleteInitiator(context, payload) {
      await api.delete(`/initiators/${payload}`)
    },
    async apiGetEventProcessors({ commit }) {
      const result = await api.get(`/event_processors`)
      commit('setEventProcessors', result.data)
    },
    async apiCreateEventProcessors({commit}, payload){
      await api.post(`/event_processors`, payload)
    },
    async apiDeleteEventProcessors({commit}, payload){
      await api.delete(`/event_processors/${payload.name}`)
    },
    async apiGetSchedules({commit}) {
      const result = await api.get(`/schedules`)
      commit("setSchedules", result.data)
    },
    async apiCreateSchedules({commit}, payload){
      await api.post(`/schedules`, payload)
    },
    async apiUpdateSchedules({commit}, payload) {
      await api.put(`/schedules/${payload.uuid}`, payload)
    },
    async apiDeleteSchedules({commit}, payload){
      await api.delete(`/schedules/${payload.uuid}`)
    },
    async apiGetEventTypes({commit}){
      const result = await api.get(`/event_types`)
      commit("setEventTypes", result.data)
    },
    async apiCreateEventType({commit}, payload){
      await api.post(`/event_types`, payload)
    },
    async apiDeleteEventType({commit}, payload){
      await api.delete(`/event_types/${payload.type}`)
    },
    async apiGetRules({commit}, payload){
      const result = await api.get(`/rules`, { params: payload })
      commit("setRules", result.data)
      return result
    },
    async apiSimulateTest({commit}, payload){
      const result = await api.post(`/rules/simulate`, payload)
      return result
    },
    async apiClearCache({commit}, payload){
      await api.post(`/rules/cache/clear`, payload)
    },
    async apiGetProcessorAssociations({commit}){
      const result = await api.get('/processor_associations')
      let list = result.data.map(el => {
        return {
          ...el,
          uuidProcessorConfiguration: el.name || 'DEFAULT'
        }
      }).sort((a, b) => a.name.localeCompare(b.name))
      commit('setProcessorAssociations', list)
    },
    async apiDeleteProcessorAssociation({commit}, payload){
      await api.delete(`/processor_associations/${payload}`)
    },
    async apiCreateProcessorAssociation({commit}, payload){
      const initiator = payload.initiator ? payload.initiator : '_'
      const path = `/processor_associations/${initiator}/processors/${payload.eventProcessor}`
      let result = await api.post(path, payload)
      return result
    },
    async apiGetProcessorAssociationDetail({commit}, payload){
      const result = await api.get(`/processor_associations/${payload}`)
      let response = {}
      if(result.data){
        const { processorConfUuid, processorName, alias, eventType, processorContext, processorMatchers, processorParentName, executionStep, processorPriority, ...data } = result.data

        response = {
          processorConfUuid, processorName, alias, eventType, processorContext, processorMatchers, processorParentName, executionStep, processorPriority, ...data
        }
      }
      commit("setProcessorDetail", response)
      return response
    },
    async apiUpdateProcessorAssociationDetail({commit}, payload) {
      let { initiator, ...data } = payload
      await api.put(`/processor_associations/${payload.processorConfUuid}`, data)
    },
    async apiGetProcessorDefinition({commit}, payload) {
      let result = await api.get(`/processor_associations/${payload.processorConfUuid}/definitions`)
      return result.data
    },
    async apiGetSchemaMessages({commit}, payload){
      let path = payload ? `messages/schemas?q=${payload}` : 'messages/schemas'
      let result = await api.get(path)
      return result.data
    },
    async apiGetSchemaMessage({commit}, payload) {
      let result = await api.get(`messages/schemas/${payload}`)
      return result.data
    },
    async apiGetScriptValidation({commit}, payload){
      let result = await api.post('/script_validation/validate', payload)
      return result.data
    },
    async apiGetRulesLogs({commit}, payload){
      let { processorId, resumeFrom } = payload
      let encodedId = resumeFrom ? encodeURIComponent(resumeFrom) : null
      let result = await api.get(`rules/logs/ProcessorConfiguration?entity_id=${processorId}&page_size=50${ encodedId ? `&resume_from=${encodedId}` : ''}`)
      return result.data
    },
    async apiGetConfigTree({commit}, payload){
      let initiator = payload.initiator || 'DEFAULT'
      let fileName = `${initiator}-${new Date().getTime()}`
      await api.post('/infrastructure/generate-configuration-tree', payload, { responseType: 'blob' }).then(data => {
        utilsCreateTemporaryLinkToDownload(fileName, data.data, 'text/yaml', 'yaml')
      })
    },
    async apiTestProcessor({commit}, payload){
      let { uuidProcessor, ...data } = payload
      let result = await api.post(`/processor_associations/${uuidProcessor}/test`, data)
      return result.data
    },
  }
}
